import { SidePanelInterface } from '../types';
import SidePanel from './SidePanel';
import React, { useEffect, useState } from 'react';
import { CreateButton, FormGroup, InputField, Label, ModalTitle } from './SharedComponents';
import apiClient from '../services/apiClient';
import { CircularProgress, Chip } from '@mui/material';
import notify from '../services/notify';
import ReactGA from 'react-ga4';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from './ConfirmationModal';
import ManageProductsModal from './ManageProductsModal';
import CategoryIcon from '@mui/icons-material/Category';

interface ProductCategoryModalProps extends Omit<SidePanelInterface, 'children'> {
	productCategory?: any | null;
	handleHidePanel: (productCategory?: any | null) => void;
	isNewProductCategory?: boolean;
	onProductCategoryUpdate?: (productCategory: any) => void;
	onProductCategoryDelete?: (productCategoryId: number) => void;
}

interface ProductCategoryFormData {
	name: string;
	description: string;
}

interface Product {
	id: number;
	name: string;
	description?: string;
	default_price?: number;
}

export default function ProductCategoryModal(props: ProductCategoryModalProps) {
	const {
		productCategory,
		handleHidePanel,
		isNewProductCategory,
		onProductCategoryUpdate,
		onProductCategoryDelete,
		...sidePanelProps
	} = props;

	const [formData, setFormData] = useState<ProductCategoryFormData>({
		name: '',
		description: '',
	});
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isManageProductsOpen, setIsManageProductsOpen] = useState<boolean>(false);
	const [productsInCategory, setProductsInCategory] = useState<Product[]>([]);
	const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(false);

	useEffect(() => {
		if (productCategory) {
			setFormData({
				name: productCategory.name || '',
				description: productCategory.description || '',
			});
			if (productCategory.id) {
				fetchProductsInCategory();
			}
		} else {
			setFormData({
				name: '',
				description: '',
			});
			setProductsInCategory([]);
		}
	}, [productCategory]);

	// Reset form when modal opens for new category
	useEffect(() => {
		if (sidePanelProps.isVisible && isNewProductCategory) {
			setFormData({
				name: '',
				description: '',
			});
			setProductsInCategory([]);
		}
	}, [sidePanelProps.isVisible, isNewProductCategory]);

	const fetchProductsInCategory = async () => {
		if (!productCategory?.id) return;

		setIsLoadingProducts(true);
		try {
			const response = await apiClient.get(`/product-categories/${productCategory.id}/products`);
			setProductsInCategory(response.data || []);
		} catch (error) {
			console.error('Error fetching products in category:', error);
		} finally {
			setIsLoadingProducts(false);
		}
	};

	const handleSubmit = async () => {
		if (!formData.name.trim()) {
			notify('error', 'Category name is required');
			return;
		}

		setIsSubmitting(true);
		try {
			const categoryData = {
				name: formData.name.trim(),
				description: formData.description.trim() || null,
			};

			let response;
			if (isNewProductCategory) {
				response = await apiClient.post('/product-categories', categoryData);
				notify('success', 'Product category created successfully');
				ReactGA.event({
					category: 'Product Categories',
					action: 'Created Product Category',
					label: `Category: ${formData.name}`,
				});
			} else {
				response = await apiClient.update(`/product-categories/${productCategory.id}`, categoryData);
				notify('success', 'Product category updated successfully');
				ReactGA.event({
					category: 'Product Categories',
					action: 'Updated Product Category',
					label: `Category ID: ${productCategory.id}`,
				});
			}

			// Extract the actual data from the response
			const result = response.data || response;

			if (onProductCategoryUpdate) {
				onProductCategoryUpdate(result);
			}
			handleHidePanel(result);
		} catch (error: any) {
			console.error('Error saving product category:', error);
			notify('error', error.response?.data?.message || 'Failed to save product category');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!productCategory?.id) return;

		setIsDeleting(true);
		try {
			await apiClient.delete(`/product-categories/${productCategory.id}`);
			notify('success', 'Product category deleted successfully');
			ReactGA.event({
				category: 'Product Categories',
				action: 'Deleted Product Category',
				label: `Category ID: ${productCategory.id}`,
			});

			if (onProductCategoryDelete) {
				onProductCategoryDelete(productCategory.id);
			}
			handleHidePanel();
		} catch (error: any) {
			console.error('Error deleting product category:', error);
			notify('error', error.response?.data?.message || 'Failed to delete product category');
		} finally {
			setIsDeleting(false);
			setIsDeleteDialogOpen(false);
		}
	};

	const handleManageProducts = () => {
		setIsManageProductsOpen(true);
	};

	const handleProductsUpdated = () => {
		fetchProductsInCategory();
	};

	const handleFormChange = (field: keyof ProductCategoryFormData, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	return (
		<>
			<SidePanel
				{...sidePanelProps}
				handleHidePanel={handleHidePanel}
				headerComponents={
					!isNewProductCategory && productCategory
						? [
								<CreateButton
									key="manage-products"
									onClick={handleManageProducts}
									outline
									style={{ fontSize: '14px', padding: '8px 16px' }}
								>
									Manage Products
								</CreateButton>,
						  ]
						: undefined
				}
			>
				<ModalTitle>
					<CategoryIcon style={{ marginRight: '8px' }} />
					{isNewProductCategory ? 'Create Product Category' : 'Edit Product Category'}
				</ModalTitle>

				<FormGroup>
					<Label>Category Name*</Label>
					<InputField
						type="text"
						value={formData.name}
						onChange={(e) => handleFormChange('name', e.target.value)}
						placeholder="Enter category name"
						required
					/>
				</FormGroup>

				<FormGroup>
					<Label>Description</Label>
					<InputField
						type="text"
						value={formData.description}
						onChange={(e) => handleFormChange('description', e.target.value)}
						placeholder="Optional description for this category"
					/>
				</FormGroup>

				{!isNewProductCategory && productCategory && (
					<FormGroup>
						<Label>Category Information</Label>
						<div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
							<Chip
								label={`${productsInCategory.length} product${
									productsInCategory.length !== 1 ? 's' : ''
								}`}
								color="primary"
								variant="outlined"
								size="small"
							/>
							{isLoadingProducts && <CircularProgress size={16} />}
						</div>
					</FormGroup>
				)}

				<div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
					<CreateButton
						onClick={handleSubmit}
						disabled={isSubmitting || !formData.name.trim()}
						style={{ flex: 1, justifyContent: 'center' }}
					>
						{isSubmitting && <CircularProgress size={16} style={{ marginRight: '8px' }} />}
						{isNewProductCategory ? 'Create Category' : 'Update Category'}
					</CreateButton>

					{!isNewProductCategory && (
						<CreateButton
							onClick={() => setIsDeleteDialogOpen(true)}
							disabled={isDeleting || productsInCategory.length > 0}
							style={{
								backgroundColor: '#ef4444',
								borderColor: '#ef4444',
								opacity: productsInCategory.length > 0 ? 0.5 : 1,
							}}
						>
							<DeleteIcon style={{ fontSize: '16px' }} />
						</CreateButton>
					)}
				</div>
			</SidePanel>

			{isDeleteDialogOpen && (
				<ConfirmationModal
					isOpen={isDeleteDialogOpen}
					onCancel={() => setIsDeleteDialogOpen(false)}
					onConfirm={handleDelete}
					title="Delete Product Category"
					message={`Are you sure you want to delete "${productCategory?.name}"? This action cannot be undone.`}
					confirmText="Delete"
					cancelText="Cancel"
					isLoading={isDeleting}
					variant="danger"
				/>
			)}

			{isManageProductsOpen && (
				<ManageProductsModal
					isOpen={isManageProductsOpen}
					onClose={() => setIsManageProductsOpen(false)}
					productCategory={productCategory}
					onProductsUpdated={handleProductsUpdated}
				/>
			)}
		</>
	);
}
